import type { SetupSection } from './fieldTypes';

const ABN = { regex: /^\d{2} ?\d{3} ?\d{3} ?\d{3}$/, message: 'ABN is 11 digits' };
const EMAIL = { regex: /^[^@\s]+@[^@\s]+\.[^@\s]+$/, message: 'Enter a valid email address' };
const COLOUR = { regex: /^#[0-9a-fA-F]{6}$/, message: 'Use a hex colour, e.g. #143c34' };

export const FUND_SECTIONS: SetupSection[] = [
  {
    id: 'trust',
    group: 'FUND',
    title: 'Trust details',
    summary: 'The issuer: the trust, its trustee, tax treatment and core terms.',
    blocks: [
      {
        title: 'Trust',
        fields: [
          { key: 'fund.name', label: 'Trust name', kind: 'text', required: true, wide: true },
          {
            key: 'fund.code',
            label: 'Fund code',
            kind: 'text',
            required: true,
            pattern: { regex: /^[A-Z0-9]{3,6}$/, message: '3 to 6 capital letters or digits' },
            help: 'Used in certificate numbers, login links and references.',
          },
          {
            key: 'fund.entityKind',
            label: 'Entity type',
            kind: 'select',
            options: [{ value: 'TRUST', label: 'Trust' }],
          },
          {
            key: 'fund.entityType',
            label: 'Entity subtype',
            kind: 'select',
            required: true,
            options: [
              { value: 'AMIT', label: 'Managed investment trust (AMIT)' },
              { value: 'MIT', label: 'Managed investment trust (not AMIT)' },
              { value: 'IDPS', label: 'IDPS' },
              { value: 'NOMINEE', label: 'Nominee / bare trust' },
              { value: 'DIV6', label: 'Trust (Division 6)' },
            ],
            toConfirm: true,
            help: 'Drives the tax statement format (AMMA vs standard).',
          },
          { key: 'fund.trustee', label: 'Trustee', kind: 'text', required: true, wide: true },
          { key: 'fund.abn', label: 'Trust ABN', kind: 'text', required: true, pattern: ABN },
          {
            key: 'fund.currency',
            label: 'Currency',
            kind: 'select',
            options: [{ value: 'AUD', label: 'AUD' }],
          },
        ],
      },
      {
        title: 'Optional identifiers',
        description: 'Only needed if the trust has them.',
        fields: [
          { key: 'fund.arsn', label: 'ARSN', kind: 'text' },
          {
            key: 'fund.partnershipRegistration',
            label: 'Partnership registration number',
            kind: 'text',
          },
          { key: 'fund.giin', label: 'GIIN (FATCA)', kind: 'text' },
          { key: 'fund.asicKey', label: 'ASIC corporate key', kind: 'text' },
        ],
      },
      {
        title: 'Fund terms',
        fields: [
          {
            key: 'fund.strategy',
            label: 'Strategy',
            kind: 'select',
            required: true,
            help: 'Suggested from the deal’s capital type; choose it if the deal has none.',
            options: ['Private Credit', 'Opportunistic', 'Core'].map((value) => ({
              value,
              label: value,
            })),
          },
          {
            key: 'fund.basis',
            label: 'Unit basis',
            kind: 'select',
            options: [
              { value: 'FIXED_UNITS', label: '$1.00 fixed units' },
              { value: 'NAV_UNITS', label: 'NAV-priced units' },
              { value: 'COMMITMENT', label: 'Commitment / capital calls' },
            ],
          },
          {
            key: 'fund.frequency',
            label: 'Distribution frequency',
            kind: 'select',
            options: [
              { value: 'MONTHLY', label: 'Monthly' },
              { value: 'QUARTERLY', label: 'Quarterly' },
              { value: 'SEMI_ANNUAL', label: 'Half-yearly' },
              { value: 'ANNUAL', label: 'Annual' },
            ],
          },
          { key: 'fund.targetSize', label: 'Target size ($)', kind: 'number', required: true },
          { key: 'fund.inceptionDate', label: 'First close', kind: 'date', required: true },
          { key: 'fund.maturityDate', label: 'Maturity (if fixed term)', kind: 'date' },
          {
            key: 'tax.aiirType',
            label: 'Investment type for ATO reporting (AIIR)',
            kind: 'select',
            options: [
              { value: 'UNIT_TRUST', label: 'Units in a unit trust' },
              { value: 'INTEREST', label: 'Interest-bearing investment' },
            ],
            toConfirm: true,
          },
          { key: 'fund.summary', label: 'Fund summary', kind: 'textarea', wide: true },
        ],
      },
    ],
  },
  {
    id: 'contacts',
    group: 'FUND',
    title: 'Contacts & registrar',
    summary: 'Who investors contact, who runs the account, and who keeps the register.',
    blocks: [
      {
        title: 'Investor contact details',
        description: 'Shown to investors on statements, emails and the investor centre.',
        fields: [
          {
            key: 'contact.email',
            label: 'Email',
            kind: 'text',
            required: true,
            pattern: EMAIL,
            toConfirm: true,
          },
          { key: 'contact.phone', label: 'Phone', kind: 'text' },
          { key: 'contact.address', label: 'Postal address', kind: 'address', wide: true },
          { key: 'contact.website', label: 'Website', kind: 'text' },
        ],
      },
      {
        title: 'Account contact',
        description:
          'Defaults to you. Choose another staff member to fill in their details from their profile in Settings → Users.',
        fields: [
          { key: 'account.userId', label: 'Staff member', kind: 'staff', required: true },
          { key: 'account.name', label: 'Name', kind: 'text', required: true },
          { key: 'account.role', label: 'Role', kind: 'text' },
          { key: 'account.email', label: 'Email', kind: 'text', required: true, pattern: EMAIL },
          { key: 'account.phone', label: 'Phone', kind: 'text' },
          { key: 'account.address', label: 'Address', kind: 'address', wide: true },
        ],
      },
      {
        title: 'Directors of the fund manager',
        fields: [
          {
            key: 'directors',
            label: 'Directors (one per line)',
            kind: 'textarea',
            wide: true,
            toConfirm: true,
            help: 'Usually filled from Settings, Default Values.',
          },
          {
            key: 'directors.placeOfBusiness',
            label: 'Place of business',
            kind: 'address',
            wide: true,
          },
        ],
      },
      {
        title: 'Billing, privacy and registrar',
        fields: [
          { key: 'billing.entity', label: 'Billing entity', kind: 'text', required: true },
          { key: 'privacy.url', label: 'Privacy policy URL', kind: 'text' },
          {
            key: 'registrar.mode',
            label: 'Registrar',
            kind: 'select',
            options: [
              { value: 'SELF', label: 'Self-administered on the Fides platform' },
              { value: 'THIRD_PARTY', label: 'Third-party registrar' },
            ],
          },
          {
            key: 'registrar.name',
            label: 'Registrar name',
            kind: 'text',
            required: true,
            showIf: (values) => values['registrar.mode'] === 'THIRD_PARTY',
          },
        ],
      },
    ],
  },
  {
    id: 'branding',
    group: 'FUND',
    title: 'Branding',
    summary: 'How the fund looks to investors: login link, colours and logo.',
    blocks: [
      {
        title: 'Investor-facing brand',
        fields: [
          { key: 'brand.website', label: 'Website', kind: 'text' },
          { key: 'brand.loginUrl', label: 'Branded login link', kind: 'text', required: true },
        ],
      },
      {
        title: 'Colours',
        columns: 3,
        fields: [
          {
            key: 'brand.colour',
            label: 'Brand colour',
            kind: 'colour',
            required: true,
            pattern: COLOUR,
          },
          {
            key: 'brand.emailHeader',
            label: 'Email header colour',
            kind: 'colour',
            pattern: COLOUR,
          },
          {
            key: 'brand.emailButton',
            label: 'Email button colour',
            kind: 'colour',
            pattern: COLOUR,
          },
        ],
      },
    ],
  },
];
